import * as React from 'react';
import { Responsive } from '@cian/ui-kit';
import classNames from 'classnames';
import { throttle } from 'throttle-debounce';

import * as styles from './styles.css';
import { getElementRect, getRightScrollOffset, getLeftScrollOffset, scrollToPos } from './utils';
import { SCROLL_THROTTLING_DELAY, SCROLL_CLICK_DEAD_ZONE } from '../../constants/verticalSlider';

export interface IVerticalSliderProps {
  leftArrow: React.FC<{ onClick(): void }>;
  rightArrow: React.FC<{ onClick(): void }>;
  scrollOffset?: number;
  onItemClick?(index: number): void;
}

export type TVerticalSliderProps = React.PropsWithChildren<IVerticalSliderProps>;

interface IVerticalSliderState {
  leftArrowVisible: boolean;
  rightArrowVisible: boolean;
}

export class VerticalSlider extends React.Component<TVerticalSliderProps, IVerticalSliderState> {
  private scrollRef = React.createRef<HTMLUListElement>();
  private startX: number;
  private scrollLeft: number;
  private isDragging = false;

  public state = {
    leftArrowVisible: false,
    rightArrowVisible: false,
  };

  public componentDidMount() {
    this.changeControlsVisibility();

    window.addEventListener('resize', this.changeControlsVisibility);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.changeControlsVisibility);
  }

  public render() {
    const { leftArrow: LeftArrow, rightArrow: RightArrow } = this.props;
    const { leftArrowVisible, rightArrowVisible } = this.state;

    return (
      <div className={styles['wrapper']}>
        <ul
          className={styles['container']}
          ref={this.scrollRef}
          onMouseDown={this.handleMouseDown}
          onScroll={this.throttledHandleScroll}
          data-testid="VerticalSliderList"
        >
          {React.Children.map(
            this.props.children,
            (child, i) =>
              child && (
                <li key={i} className={styles['item']} onClick={this.handleItemClick(i)}>
                  {child}
                </li>
              ),
          )}
        </ul>
        <Responsive desktop>
          <div className={styles['controls']}>
            {leftArrowVisible && (
              <div className={classNames(styles['arrow'], styles['left-arrow'])}>
                <LeftArrow onClick={this.onLeftArrowClick} />
              </div>
            )}
            {rightArrowVisible && (
              <div className={classNames(styles['arrow'], styles['right-arrow'])}>
                <RightArrow onClick={this.onRightArrowClick} />
              </div>
            )}
          </div>
        </Responsive>
      </div>
    );
  }

  private handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();

    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      this.startX = e.pageX - containerRect.left;
      this.scrollLeft = container.scrollLeft;

      window.addEventListener('mousemove', this.handleMove);
      window.addEventListener('mouseup', this.handleMoveEnd);
    }

    this.isDragging = false;
  };

  private handleMoveEnd = () => {
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mousemove', this.handleMoveEnd);
  };

  private handleItemClick = (i: number) => (e: React.MouseEvent) => {
    if (this.isDragging) {
      e.stopPropagation();

      return;
    }

    const { onItemClick } = this.props;

    /* istanbul ignore else */
    if (onItemClick) {
      onItemClick(i);
    }
  };

  private handleMove = (e: MouseEvent) => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      if (Math.abs(e.pageX - this.startX) >= SCROLL_CLICK_DEAD_ZONE) {
        this.isDragging = true;
      }

      const x = e.pageX - containerRect.left;
      const walk = x - this.startX;

      container.scrollLeft = this.scrollLeft - walk;

      if (container.scrollLeft <= 0) {
        this.startX = x;
        this.scrollLeft = 0;
      }

      const hiddenWidth = container.scrollWidth - containerRect.width;

      if (container.scrollLeft >= hiddenWidth) {
        this.startX = x;
        this.scrollLeft = hiddenWidth;
      }
    }
  };

  private changeControlsVisibility = () => {
    const container = this.scrollRef.current;
    const { leftArrowVisible, rightArrowVisible } = this.state;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      const leftChildRect = getElementRect(container.firstChild as HTMLLIElement);
      const rightChildRect = getElementRect(container.lastChild as HTMLLIElement);

      const leftBorderPosition = container.scrollLeft;
      const rightBorderPosition = containerRect.width + container.scrollLeft;
      const showLeftArrow = leftChildRect.left - containerRect.left < leftBorderPosition;
      const showRightArrow = rightChildRect.right - containerRect.left > rightBorderPosition;

      if (leftArrowVisible !== showLeftArrow) {
        this.setState({ leftArrowVisible: showLeftArrow });
      }

      if (rightArrowVisible !== showRightArrow) {
        this.setState({ rightArrowVisible: showRightArrow });
      }
    }
  };

  private throttledHandleScroll = throttle(SCROLL_THROTTLING_DELAY, this.changeControlsVisibility);

  private onRightArrowClick = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getRightScrollOffset(container, this.props.scrollOffset || 0) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };

  private onLeftArrowClick = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getLeftScrollOffset(container, this.props.scrollOffset || 0) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };
}
